import { isEmpty, isObject, sortBy, isEqual } from 'underscore';
import { toastr } from 'react-redux-toastr';
import { push } from 'redux-first-history';
import { format } from 'date-fns';
import { AxiosError, AxiosResponse } from 'axios';
import routesApi from '@/apis/routesApi';
import { endFetching, groupRoutesBy, startFetching } from '../common/commonPure';
import { errorConfig, i18next } from '@/config/i18n';
import { setColors } from '@/components/Common/colorManager';
import jobsApi from '@/apis/jobsApi';
import { groupingTypes, processTypes } from '../types';
import {
  focusRoute, routeUpdate, setCache, setDayData, setPagination, setRoutes, setRouteStrings, setSingleTourView, updateCache,
} from './routesPure';
import { focusListItem, normalize, setJobSelection } from '../jobs/jobsPure';
import processApi from '@/apis/processApi';
import { CNAME } from '@/config/paths';
import { AppDispatch, RootState } from '../../index';
import {Color, DayDataType, Route, RoutePolyline, TLRange} from '@/config/types/Routes';
import { DispatchThunkAction, PaginationResponse, Planning, ProcessHistory } from '@/config/types/Common';
import {
  setVehiclesData
} from '../vehicles/pure'
import { LatLngTuple } from 'leaflet';
import utilMaps from '@/components/utils/utilMaps';

export function getRoutes(
  range: TLRange,
  multi: boolean,
  force?: boolean,
  optimization_id?: string
) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<{
    routes: Route[];
    colors: { [key: string]: Color };
  }> => {
    const {
      routes: { cache, pagination },
      routing,
      core: { selected_process },
    } = getState();
    dispatch(startFetching());
    const { startDate, endDate } = range;
    const cacheKey = `${selected_process.optimization_id}`;
    const cacheMatch = cache[cacheKey];
    try {
      if (cacheMatch && !optimization_id && !force) {
        dispatch(
          setDayData({
            ...cacheMatch,
            available: true,
          })
        );
        return Promise.resolve(cacheMatch);
      }
      const params = {
        page: 1,
        max: pagination.max,
        optimization_id,
        multi,
      };
      const {
        data: { data: newRoutes, ...newPagination },
      } = await routesApi.getRoutesByRange(range, params);
      const {
        data: { data: routestrings },
      } = await routesApi.getRouteStringByRange(range);
      const routeStringSet = normalize<RoutePolyline>(routestrings);

      const res = sortBy(newRoutes, (x) => x.initial_creationtime);
      const routes = normalize(res);
      const available = !isEmpty(routes);
      const colors = setColors(Object.values(routestrings));
      const route_ids = res.map(({ id }) => id);
      const paginationNew = {
        ...pagination,
        ...newPagination,
        objects: route_ids,
        results_per_page: 20,
      };
      const newCache = {
        [cacheKey]: {
          range,
          colors,
          pagination: {
            ...newPagination,
            objects: route_ids,
            results_per_page: 20,
          },
          routes,
          routestrings: routeStringSet,
          available,
        },
      };
      dispatch(
        setDayData({
          colors,
          available,
          routes,
          routestrings: routeStringSet,
        })
      );
      return {
        routes: res,
        colors,
      };
    } catch (err: unknown) {
      const error = err as AxiosError;
      if (error.response) {
        if (error.response.status === 404) {
          const { [cacheKey]: trash, ...newCache } = cache;
          dispatch(
            setDayData({
              colors: {},
              available: false,
              routes: {},
              routestrings: {}
            })
          );
        }
      }
      return Promise.reject(error);
    } finally {
      dispatch(endFetching());
    }
  };
}

export function getRoutesByProcess(
  selected_process: Planning,
  multi: boolean = true,
  view: 'Timeline' | 'Map'
) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<{
    routes: Route[];
    colors: { [key: string]: Color };
  }> => {
    const {
      routes: { cache, pagination, timelineRange, routestrings, colors }
    } = getState();
    const cacheKey = `${selected_process.optimization_id}`;
    const cacheMatch = cache[cacheKey];
    let colorsData = colors;
    dispatch(startFetching());
    //  Only use cache for Timeline view
    if (view === 'Timeline' && cacheMatch) {
      dispatch(setDayData({ ...cacheMatch, available: true }));
      dispatch(endFetching());
      return Promise.resolve(cacheMatch);
    }
    try {
      const params = { optimization_id: selected_process.optimization_id };
      const {
        data: { routes: newRoutes, vehicles, ...newPagination }
      } = await routesApi.getRoutesByProcess(params);
      dispatch(setVehiclesData(vehicles));
      const allItems = Object.values(newRoutes).flat();
      //@ts-ignore
      const sortedItems = allItems.sort((x) => x.start_time);
      const routes = normalize(sortedItems);
      const route_ids = sortedItems.map((item: any) => item.id);
      const available = !isEmpty(routes);
      const paginationNew = {
        ...pagination,
        ...newPagination,
        objects: route_ids,
        max: 20,
      };
      const newCache = {
        [selected_process.optimization_id]: {
          optimization_id: selected_process.optimization_id,
          colors: colorsData,
          pagination: paginationNew,
          routes,
          range: timelineRange,
          routestrings: {},
          available,
        },
      };

      //@ts-ignore
      dispatch(setCache(newCache));
      dispatch(
        setDayData({
          colors: colorsData,
          available,
          //@ts-ignore
          routes,
          routestrings: {}, 
        })
      );
      return {
        //@ts-ignore
        routes: sortedItems,
        colors: colorsData,
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        dispatch(
          setDayData({
            colors: {},
            available: false,
            routes: {},
            routestrings: {},
          })
        );
      }
      return Promise.reject(error);
    } finally {
      dispatch(endFetching());
    }
  };
}

export function lazyRouteUpdate(page: number) {

  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<Route[]> => {
    const {
      routes: existingRoutes,
      pagination,
      cache,
    } = getState().routes;

    const { selected_process } = getState().core;
    if (pagination.page === page || page > pagination.total_pages) return;
    if (!Number.isInteger(page)) {
      return dispatch(
        setPagination({
          page: 0,
          total_elements: 0,
          total_pages: 0,
          results_per_page: 20,
          objects: [],
        })
      );
    }
    dispatch(startFetching());
    try {
      const params = {
        ...pagination,
        page,
        optimization_id: selected_process.optimization_id,
        multi: true,
      };

      const {
        data: { objects: routes, ...newPagination },
      }: { data: PaginationResponse<Route> } =
        await routesApi.getRoutesByProcess(params);
      const res: Route[] = sortBy(routes, (x: Route) => x.initial_creationtime);
      const newRoutes = { ...normalize(res), ...existingRoutes };
      const newPageData = res.map(({ id }: Route) => id);
      dispatch(setRoutes(newRoutes, true));
      dispatch(
        setPagination({
          ...pagination,
          ...newPagination,
          objects: newPageData,
        })
      );
      const cacheKey = `${selected_process.optimization_id}`;
      const cacheContent = cache[cacheKey];

      const cacheData = {
        routes: newRoutes,
        available: true,
        pagination: {
          ...pagination,
          ...newPagination,
          objects: [...cacheContent.pagination.objects, ...newPageData],
        },
      };

      const newCache = {
        [cacheKey]: {
          ...cache[cacheKey],
          ...cacheData,
        },
      };
      dispatch(setCache(newCache));
      dispatch(endFetching());
      return res;
    } catch (error: unknown) {
      const err = error as AxiosError;
      dispatch(endFetching());
    }
  };
}

export function removeAllTours() {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<Route[]> => {
    const { timelineRange } = getState().routes;
    dispatch(startFetching());
    return routesApi
      .cancelRoutesByDate(timelineRange)
      .then(({ data: { success, message } }: AxiosResponse) => {
        if (success) {
          toastr.success(i18next.t('common:routes.tours'), message);
          dispatch(getRoutes(timelineRange, false, false, undefined));
        } else {
          toastr.error(i18next.t('common:routes.tours'), message, errorConfig);
        }
        return success;
      })
      .catch((err) => {
        console.warn(err);
        toastr.error(
          i18next.t('common:state.error'),
          i18next.t('common:routes.deleteAllErr'),
          errorConfig
        );
      })
      .finally(() => dispatch(endFetching()));
  };
}

export function viewSingleRoute(
  route: Route,
  color: string,
  routestring: string
) {
  return (
    dispatch: AppDispatch
  ): Promise<{ route: Route; color: string; routestring: string }> => {
    dispatch(setSingleTourView(route, color, routestring));
    if (route) {
      dispatch(push(`${CNAME}routes/${route.id}`));
    }
    return Promise.resolve({ route, color, routestring });
  };
}

export function getRouteStrings(custom_range: TLRange) {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<any> => {
    const { timelineRange: range } = getState().routes;
    try {
      const {
        data: { data },
      } = await routesApi.getRouteStringByRange(custom_range || range);
      const routestringSet: { [key: string]: string } = normalize<string>(data);
      dispatch(setRouteStrings(routestringSet));
      return Promise.resolve(routestringSet);
    } catch (err) {
      console.warn('getRouteStrings', err);
    }
  };
}

export function dropDeliveries() {
  return (
    dispatch: AppDispatch,
    getState: () => RootState
  ): DispatchThunkAction<DispatchThunkAction<any>> => {
    dispatch(startFetching());
    const { active_list_tab, ...jobReducerValues } = getState().jobs;
    const {
      focused_route: { route },
    } = getState().routes;
    const source = active_list_tab ? 'marked_jobs' : 'selected_jobs';
    const { [source]: jobs } = jobReducerValues;
    return dispatch(
      dispatchRouteDeliveries('add_deliveryids', jobs, route?.id)
    );
  };
}

export function detachDeliveriesFromTour(deliveryID: number) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      focused_route: { route },
    } = getState().routes;
    dispatch(startFetching());
    const {
      deliveries: { deliveriesById },
    } = getState().jobs;
    const fallbackRoute = { id: deliveriesById[deliveryID]?.route_id };
    return dispatch(
      dispatchRouteDeliveries(
        'del_deliveryids',
        [deliveryID],
        route?.id || fallbackRoute.id
      )
    );
  };
}

export const onFocusRoute = (route: Route, color: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      timelineRange: { startDate, endDate },
      routes,
      cache,
    } = getState().routes;
    const {
      deliveries: { deliveriesById },
    } = getState().jobs;
    //@ts-ignore
    dispatch(groupRoutesBy(groupingTypes.ROUTE));
    const cacheKey = `${startDate.toString()}_${endDate.toString()}`;
    const cacheMatch = cache[cacheKey];
    if (!route || !color) {
      if (cacheMatch) {
        dispatch(
          setDayData({
            ...cacheMatch,
            available: true,
          })
        );
      }
      dispatch(focusRoute(null, null, []));
      return Promise.resolve();
    } else {
      const selected = routes[route.id];
    }
  };
};

/**
 *
 * @param {string} action - either del_deliveryids or add_deliveryids
 * @param {array<number>} ids - ids to add or remove
 * @param {number} routeId - route id to patch
 *
 */
export function dispatchRouteDeliveries(
  action: 'del_deliveryids' | 'add_deliveryids',
  ids: number[],
  routeId: string | number
) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const {
        timelineRange: range,
        focused_route,
        pagination,
        routes,
        colors,
        routestrings,
      } = getState().routes;
      const {
        view: { showTours, showTimeline },
      } = getState().jobs;
      const {
        data: { new_route_id, message },
      } = await jobsApi.updateRouteAsync(routeId, { [action]: ids });
      toastr.success('Route ' + new_route_id, message);
      dispatch(setJobSelection([]));
      const { data } = await routesApi.asyncGETRouteByID(new_route_id);
      const newDayData: DayDataType = updateRangeData({
        pagination,
        range,
        data,
        routeId,
        routes,
        colors,
        routestrings,
      });
      if (isObject(newDayData)) {
        dispatch(setDayData(newDayData));
        if (focused_route.route && (showTimeline || showTours)) {
          await dispatch(
            onFocusRoute(data, newDayData.colors[new_route_id]?.color)
          );
        }
      }
      dispatch(routeUpdate({ ...data, old_id: routeId }));

      dispatch(focusListItem(null));
      return Promise.resolve(true);
    } catch (err) {
      toastr.error(
        i18next.t('common:routes.planTour'),
        i18next.t('common:routes.group.replanErr'),
        errorConfig
      );
      return Promise.reject(err);
    } finally {
      dispatch(endFetching());
    }
  };
}

function updateRangeData(props: any): DayDataType {
  const { pagination, range, data, routeId, routes, colors, routestrings } =
    props;
  if (isEmpty(routes) && isEmpty(colors) && isEmpty(routestrings)) return props;
  const newPageData = [...pagination.objects, data.id].filter(
    (id) => id === routeId
  );
  const cacheKey = `${range.startDate.toString()}_${range.endDate.toString()}`;
  const { [routeId]: routeToReplace, ...otherRoutes } = routes;
  const { [routeId]: stringToReplace, ...otherStrings } = routestrings;
  const { [routeId]: colorToReplace, ...otherColors } = colors;

  otherRoutes[data.id] = data;
  otherStrings[data.id] = { ...routestrings[routeId], id: data.id };
  otherColors[data.id] = { ...colors[routeId], id: data.id };

  const dayData = {
    range,
    routes: otherRoutes,
    pagination: {
      ...pagination,
      objects: newPageData,
    },
    colors: otherColors,
    routestrings: otherStrings,
    available: true,
  };

  const newCache = {
    [cacheKey]: {
      ...dayData,
      pagination: dayData.pagination,
    },
  };
  return {
    ...dayData,
  };
}

export function recalculate(id: string | number, data: any = {}) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      timelineRange: range,
      cache,
      pagination,
      routes,
      colors,
      routestrings,
    } = getState().routes;
    return jobsApi
      .updateRouteAsync(id, data)
      .then(({ data: { new_route_id } }) => {
        toastr.success(
          i18next.t('common:routes.planTour'),
          i18next.t('common:routes.group.replanSuccess')
        );
        return routesApi
          .asyncGETRouteByID(new_route_id)
          .then(({ data: newRoute }) => {
            const dayData = updateRangeData({
              pagination,
              range,
              data: newRoute,
              id,
              routes,
              colors,
              routestrings,
            });
            if (isObject(dayData)) {
              dispatch(setDayData(dayData));
            }
            dispatch(routeUpdate({ ...newRoute, old_id: id }));
            return Promise.resolve();
          });
      })
      .catch((err) => {
        console.warn('rePlanTour err ', err);
        toastr.error(
          'Tour' + id,
          i18next.t('common:routes.group.replanErr'),
          errorConfig
        );
        return Promise.reject(err);
      });
  };
}

// TODO
// UPDATE ALL REFERNECES {COLORS; ROUTES; ROUTESTRINGS; CACHE}
export function patchTour(
  id: number | string,
  routeUpdateData: any
): DispatchThunkAction<Promise<Route>> {
  return (dispatch: AppDispatch, getState: () => RootState): Promise<any> => {
    const { cache, routes } = getState().routes;
    const { selected_process } = getState().core;
    return routesApi
      .patchRoute(id, routeUpdateData)
      .then(({ data }) => {
        const newRoutes = { ...routes };
        const cacheKey = `${selected_process.optimization_id}`;
        const cacheMatch = { ...cache[cacheKey] };
        newRoutes[id] = data;
        cacheMatch.routes[id] = data;
        dispatch(routeUpdate({ ...data, old_id: id }));
        dispatch(updateCache({ [cacheKey]: cacheMatch }));
        return Promise.resolve(data);
      })
      .catch((err) => {
        console.warn(err);
        return Promise.reject(err);
      });
  };
}

export function removeRoute(id: string | number) {
  return (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<number | string | void> => {
    const {
      pagination,
      cache,
      colors,
      routes,
      routestrings,
    } = getState().routes;
    const { selected_process } = getState().core;
    return routesApi
      .archiveRoute(id)
      .then(() => {
        toastr.success(
          'Tour ' + id,
          i18next.t('common:routes.group.deleteTourSuccess')
        );
        const cacheKey = `${selected_process.optimization_id}`;
        const cacheMatch = { ...cache[cacheKey] };
        cacheMatch.pagination.total_elements--;
        pagination.total_elements--;
        const { [id]: toRemove, ...otherRoutes } = routes;
        const { [id]: routeStringToRemove, ...otherStrings } = routestrings;
        const { [id]: colorToRemove, ...otherColors } = colors;
        const { [id]: cacheRouteToRemove, ...otherCacheRoutes } =
          cacheMatch.routes;
        cacheMatch.routes = otherCacheRoutes;
        dispatch(
          setDayData({
            routes: otherRoutes,
            deleted: id,
            routestrings: otherStrings,
            colors: otherColors,
            available: true
          })
        );
        return id;
      })
      .catch((err) => {
        console.warn('archiveTour Err ', err);
        toastr.error(
          'Tour ' + id,
          i18next.t('common:routes.group.deleteTourErr'),
          errorConfig
        );
      });
  };
}
/**
 * @param {TLRange} [range] - an optional range object holding start and end date boundaries for the process we want to retrieve.
 **/
export function getProcessByDate(range: TLRange) {
  return (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<number | string | void> => {
    return processApi
      .getProcessIds(range)
      .then(({ data }) => {
        const succeeded = data.filter(
          (item: ProcessHistory) => item.state === processTypes.SUCCESS
        );
        return succeeded;
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          toastr.error(
            i18next.t('common:state.error'),
            err.response.data.message,
            errorConfig
          );
        }
      });
  };
}

export function getRouteDates() {
  return (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<number[]> => {
    const {
      timelineRange: { startDate },
    } = getState().routes;
    const month = format(new Date(startDate), 'yyyy-MM');
    return routesApi
      .getRoutesInMonth(month)
      .then(({ data: { route_days_per_month } }) =>
        Promise.resolve(route_days_per_month)
      )
      .catch((err) => {
        return Promise.reject(err);
      });
  };
}
